/* eslint-disable jsx-a11y/media-has-caption */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import {
    RemoteAudioTrack,
    RemoteParticipant,
    RemoteVideoTrack,
    RemoteAudioTrackPublication,
    RemoteVideoTrackPublication,
    RemoteTrack,
} from 'twilio-video';
import cx from 'classnames';

import { isNull } from 'lodash';
import { isChrome } from 'src/utils/browser.util';
import { useTwilioUserMediaContext, useVideoToggle, useCall } from '../../customer-call-sdk';
import VideoInput from '../VideoInput';
import InterpreterCameraOff from '../common/Icons/InterpreterCameraOff';
import CameraDisabledIcon from '../common/Icons/CameraDisabled';
import { useIdentity } from '../../customer-call-sdk/context/IdentityContext';
import { getParticipantType } from '../../services/participant.service';

const StyledRemoteParticipantVideo = styled.div`
    width: 100%;
    height: 100%;
    background-color: #000000;
    padding: 0.5rem;

    .hidden {
        display: none;
    }

    .remote-video {
        width: 100%;
        height: 100%;
    }

    .no-video-view {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .camera-disabled-icon {
        min-width: 300px;
        min-height: 300px;
        width: calc(100vh * 0.3);
        height: calc(100vh * 0.3);
        color: rgba(255, 255, 255, 0.1);
    }
`;

const StyleDisabledText = styled.div`
    width: 150px;
    height: 20px;
    text-align: center;
    font-size: 20px;
    color: white;
`;

const StyledIconContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`;

const StyledCameraDisabledIcon = styled(CameraDisabledIcon)`
    min-width: 300px;
    min-height: 300px;
    width: calc(100vh * 0.3);
    height: calc(100vh * 0.3);
    color: rgba(255, 255, 255, 0.1);
`;

const StyledAudio = styled.audio`
    display: none !important;
`;
export interface RemoteParticipantVideoProps extends React.HTMLAttributes<HTMLDivElement> {
    participant: RemoteParticipant;
    onMessage?: (text: string, from: string) => void;
    isVisible: boolean;
    enableAudio: boolean;
    volumeOff: boolean;
}
// Reference: https://github.com/microsoft/TypeScript/issues/20707
// Comment: https://github.com/microsoft/TypeScript/issues/20707#issuecomment-351874491
function notNull<T>(x: T | null): x is T {
    return x !== null;
}

const getTracksFromPublications = (
    tracks: Array<RemoteAudioTrackPublication | RemoteVideoTrackPublication>,
): Array<RemoteAudioTrack | RemoteVideoTrack> => {
    return tracks
        .map((track: RemoteAudioTrackPublication | RemoteVideoTrackPublication) => track.track)
        .filter(notNull);
};

const RemoteParticipantVideo: React.FC<RemoteParticipantVideoProps> = ({
    participant,
    onMessage,
    isVisible,
    enableAudio,
    volumeOff,
    ...props
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [audioTracks, setAudioTracks] = useState<RemoteAudioTrack[]>([]);
    const [videoTracks, setVideoTracks] = useState<RemoteVideoTrack[]>([]);
    const { isVideoEnabled } = useVideoToggle(videoTracks[0]);
    const { speakerDeviceId } = useTwilioUserMediaContext();
    const call = useCall();
    const { setRemoteVideoFlag } = useIdentity();

    const fetchParticipantType = async () => {
        if (call.requestId && participant.identity) {
            const { requestId } = call;
            const participantId = participant.identity;
            const { data } = await getParticipantType({ requestId, participantId });

            if (data && !isVideoEnabled) {
                if (
                    data.participantType === 'hoh_user' &&
                    data.deviceType === 'Mobile' &&
                    data.deviceMake === 'iOS'
                ) {
                    setRemoteVideoFlag(true);
                }
            }
        }
    };

    const handleTrackSubscribed = useCallback((track: RemoteTrack) => {
        if (track.kind === 'audio') {
            setAudioTracks((tracks) => [...tracks, track]);
        } else if (track.kind === 'video') {
            if (isChrome() && participant.identity) {
                fetchParticipantType();
            }
            setVideoTracks((tracks) => [...tracks, track]);
        } else if (track.kind === 'data') {
            track.on('message', (data) => {
                onMessage?.(data as string, participant.identity);
            });
        }
    }, []);

    const handleTrackUnsubscribed = useCallback((track: RemoteTrack) => {
        if (track.kind === 'audio') {
            const audioTrack = track;
            setAudioTracks((tracks) => [...tracks.filter((_track) => _track !== audioTrack)]);
        } else if (track.kind === 'video') {
            const videoTrack = track;
            setVideoTracks((tracks) => [...tracks.filter((_track) => _track !== videoTrack)]);
        }
    }, []);

    useEffect(() => {
        const participantAudioTracks = getTracksFromPublications([
            ...participant.audioTracks.values(),
        ]);
        setAudioTracks(participantAudioTracks as RemoteAudioTrack[]);

        const participantVideoTracks = getTracksFromPublications([
            ...participant.videoTracks.values(),
        ]);
        setVideoTracks(participantVideoTracks as RemoteVideoTrack[]);

        participant.on('trackSubscribed', handleTrackSubscribed);

        participant.on('trackUnsubscribed', handleTrackUnsubscribed);

        return () => {
            participant.off('trackSubscribed', handleTrackSubscribed);
            participant.off('trackUnsubscribed', handleTrackUnsubscribed);
        };
    }, []);

    useEffect(() => {
        const videoTrack = videoTracks[0];

        if (videoRef.current && videoTrack) {
            videoTrack.attach(videoRef.current);
        }
        return () => {
            videoTrack?.detach();
        };
    }, [videoTracks]);

    const attachAudio = useCallback(
        async (audioTrack: RemoteAudioTrack) => {
            const audioEl = audioRef.current;
            if (!audioEl) {
                return;
            }
            if (audioTrack) {
                audioTrack.attach(audioEl);
            }
            if (audioEl.setSinkId && speakerDeviceId) {
                await audioEl.setSinkId(speakerDeviceId);
            }

            if (volumeOff) {
                audioEl.muted = true;
                audioEl.volume = 0;
            } else {
                audioEl.muted = false;
                audioEl.volume = 1;
            }
        },
        [volumeOff],
    );

    useEffect(() => {
        const audioTrack = audioTracks[0];
        if (enableAudio && audioTrack) {
            attachAudio(audioTrack);
        }

        return () => {
            audioTrack?.detach();
        };
    }, [audioTracks, enableAudio, attachAudio]);
    const supportFlag = call.requestType === 'P2P_OUTBOUND' || isNull(call.interpreterName);

    return (
        <>
            <StyledRemoteParticipantVideo
                {...props}
                // className={cx({ hidden: !isVisible || videoTracks.length === 0 })}
            >
                {!isVideoEnabled && (
                    <div className="no-video-view">
                        {call.requestType === 'P2P' ||
                        call.interpreterName === '' ||
                        supportFlag ? (
                            <StyledIconContainer
                                aria-label={
                                    isVideoEnabled ? 'Camera is turned on' : 'Camera is turned off'
                                }
                            >
                                <StyledCameraDisabledIcon />
                                <StyleDisabledText>Camera is turned off.</StyleDisabledText>
                            </StyledIconContainer>
                        ) : (
                            <InterpreterCameraOff className="camera-disabled-icon" />
                        )}
                    </div>
                )}
                (
                <VideoInput
                    videoRef={videoRef}
                    resize
                    className={cx('remote-video', {
                        hidden: !isVideoEnabled,
                    })}
                />
                )
            </StyledRemoteParticipantVideo>

            <StyledAudio
                ref={audioRef}
                autoPlay
                controls={false}
                className={cx('remote-audio', { hidden: !isVisible || audioTracks.length === 0 })}
            />
        </>
    );
};

export default RemoteParticipantVideo;
